import React, { useState } from "react";
import { cls } from "@wss/cls";

import { TopperSection } from "../../../../types";

import Title from "../../../../components/Title";
import Section from "../../../../components/Section";
import Carousel from "../../../../components/Carousel";

import { getTopperVariant } from "../../../utility";

export interface MultiSectionProps {
  sections: TopperSection[];
  defaultSection: string;
}

/**
 * This component renders when more than one section is configured
 * @param sections
 * @param defaultSection
 */
const Multi = ({ sections, defaultSection }: MultiSectionProps) => {
  const [activeSection, setActiveSection] = useState<string>(defaultSection);

  return (
    <>
      <Title.Header className={cls("sm:hidden")}>
        {sections[0]!.name}
      </Title.Header>
      <Title
        className="topper-btn-container btn-group flex items-center justify-start sm:justify-center"
        role="tablist"
      >
        {sections.map(({ sectionId, name }, index) => (
          <button
            type="button"
            key={sectionId}
            id={`tab-${index + 1}`}
            role="tab"
            aria-controls={`panel-${index + 1}`}
            aria-selected={activeSection === name ? "true" : "false"}
            className={cls(
              "btn btn-small btn-light",
              "hidden border-gray-400",
              "hover:bg-gray-50",
              "sm:inline-flex",
              { "topper-active": activeSection === name },
            )}
            onClick={() => setActiveSection(name)}
          >
            {name}
          </button>
        ))}
      </Title>
      {sections.map(({ sectionId, name, displayType, items }, index) => {
        const TopperItem = getTopperVariant(displayType);
        return (
          <Section
            key={sectionId}
            data-testid={`topper-section-${sectionId}`}
            className={cls({
              hidden: activeSection !== name,
              "min-h-[80px]": [1, 2].includes(displayType),
              "min-h-[100px]": ![1, 2].includes(displayType),
            })}
            role="tabpanel"
            id={`panel-${index + 1}`}
            aria-labelledby={`tab-${index + 1}`}
          >
            <div className="relative">
              {/* This condition is essential to ensure that the intersection observer is triggered when a new tab
            is selected in the standard multi-select variant, displaying its corresponding content. */}
              {items.length > 0 && activeSection === name && (
                /* To be replaced with a zest carousel */
                <Carousel
                  displayType={displayType}
                  wrapperProps={{
                    className: cls("flex justify-start", "md:justify-center"),
                  }}
                  topperItems={items.map((item, index) => (
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <TopperItem itemIndex={index} {...item} />
                  ))}
                />
              )}
            </div>
          </Section>
        );
      })}
    </>
  );
};

export default Multi;
